import { ApolloError, useMutation } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { from } from 'rxjs'

import { CreateEmptyCartDocument } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { useStorage } from '../../../hooks/useStorage'
import { updateGuestCartId } from '../state/cart'

export type UseGuestCartId = [
  getGuestCartId: () => void,
  state: {
    cartId?: string
    loading: boolean
    error?: ApolloError | Error
    called: boolean
    recreated: boolean
    clearCartId: () => void
    recreateCartId: () => void
  },
]

export const useGuestCartId = (): UseGuestCartId => {
  const [cartId, setCartId, clearCartIdStorage] = useStorage({ key: 'guest_cart_id' })
  const [called, setCalled] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [recreated, setRecreated] = useState<boolean>(false)
  const [error, setError] = useState<ApolloError | Error | undefined>(undefined)
  // store the guest cart id in a ref to detect external changes to local storage
  // e.g. when the guest cart id is cleared after placing an order
  const cartIdRef = useRef(cartId)

  const [createEmptyCart] = useMutation(CreateEmptyCartDocument)

  useLogAndCaptureError(error)

  const getGuestCartId = useCallback(() => {
    setCalled(true)
  }, [])

  const clearCartId = useCallback(() => {
    clearCartIdStorage()
    cartIdRef.current = ''
    setCalled(false)
    // Update the guest cart ID observable when clearing
    updateGuestCartId(null)
  }, [clearCartIdStorage])

  const createEmptyCartAndUpdateState = useCallback(() => {
    setLoading(true)
    setError(undefined)
    return from(createEmptyCart()).subscribe({
      next(result) {
        const firstError = result.errors?.[0]
        if (firstError) {
          setError(new ApolloError({ graphQLErrors: [firstError] }))
          setLoading(false)
          return
        }
        const newCartId = result.data?.createEmptyCart
        if (!newCartId) {
          setError(new Error('Empty response from createEmptyCart mutation'))
          setLoading(false)
          return
        }

        setCartId(newCartId)
        cartIdRef.current = newCartId
        setLoading(false)
        // Update the guest cart ID observable when creating new cart
        updateGuestCartId(newCartId)
      },
      error(err) {
        setError(err instanceof Error ? err : new Error(`${err}`))
        setLoading(false)
      },
    })
  }, [createEmptyCart, setCartId])

  const recreateCartId = useCallback(() => {
    clearCartIdStorage()
    cartIdRef.current = ''
    setCalled(true)
    setRecreated(true)
    // Update the guest cart ID observable when recreating
    updateGuestCartId(null)
    // createEmptyCart is called by useEffect
  }, [clearCartIdStorage, setCalled])

  useEffect(() => {
    const isCartIdClearedExternally = !cartId && !!cartIdRef.current
    if (cartIdRef.current !== cartId) {
      // maintain cartIdRef.current after deciding if cartId was cleared externally
      cartIdRef.current = cartId
      // Update the guest cart ID observable when cartId changes (including from storage)
      updateGuestCartId(cartId || null)
    }
    if (!called && !isCartIdClearedExternally) {
      return
    }
    if (cartId) {
      setLoading(false)
      setError(undefined)
      return
    }
    const subscription = createEmptyCartAndUpdateState()
    return () => subscription.unsubscribe()
  }, [called, cartId, createEmptyCartAndUpdateState])

  return [
    getGuestCartId,
    {
      cartId: cartId || undefined,
      loading,
      error,
      called,
      recreated,
      clearCartId,
      recreateCartId,
    },
  ]
}
